import { useCallback, useEffect, useRef, type PointerEvent } from "react"
import { RotateCcw } from "lucide-react"
import { Button } from "@/components/ui/button"

interface SignaturePadProps {
  value: string
  onChange: (value: string) => void
}

export function SignaturePad({ value, onChange }: SignaturePadProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const drawingRef = useRef(false)
  const lastPointRef = useRef<{ x: number; y: number } | null>(null)
  const hasSignatureRef = useRef(Boolean(value))
  const valueRef = useRef(value)

  const drawStoredSignature = useCallback((dataUrl: string) => {
    const canvas = canvasRef.current
    if (!canvas) return
    const rect = canvas.getBoundingClientRect()
    const ctx = canvas.getContext("2d")
    if (!ctx) return

    const image = new Image()
    image.onload = () => {
      ctx.drawImage(image, 0, 0, rect.width, rect.height)
    }
    image.src = dataUrl
  }, [])

  const prepareCanvas = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const preservedSignature = hasSignatureRef.current ? canvas.toDataURL("image/png") : valueRef.current
    const rect = canvas.getBoundingClientRect()
    const ratio = window.devicePixelRatio || 1
    canvas.width = rect.width * ratio
    canvas.height = rect.height * ratio

    const ctx = canvas.getContext("2d")
    if (!ctx) return

    ctx.setTransform(ratio, 0, 0, ratio, 0, 0)
    ctx.lineCap = "round"
    ctx.lineJoin = "round"
    ctx.lineWidth = 2.2
    ctx.strokeStyle = "#111827"
    ctx.fillStyle = "#ffffff"
    ctx.fillRect(0, 0, rect.width, rect.height)

    if (preservedSignature) {
      hasSignatureRef.current = true
      drawStoredSignature(preservedSignature)
    }
  }, [drawStoredSignature])

  useEffect(() => {
    prepareCanvas()
    window.addEventListener("resize", prepareCanvas)
    return () => window.removeEventListener("resize", prepareCanvas)
  }, [prepareCanvas])

  useEffect(() => {
    if (value === valueRef.current) return
    valueRef.current = value

    const canvas = canvasRef.current
    const ctx = canvas?.getContext("2d")
    if (!canvas || !ctx) return

    if (!value) {
      const rect = canvas.getBoundingClientRect()
      ctx.fillStyle = "#ffffff"
      ctx.fillRect(0, 0, rect.width, rect.height)
      hasSignatureRef.current = false
      return
    }

    hasSignatureRef.current = true
    drawStoredSignature(value)
  }, [value, drawStoredSignature])

  const getPoint = (event: PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current
    if (!canvas) return null
    const rect = canvas.getBoundingClientRect()
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    }
  }

  const finishSignature = (event?: PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current
    drawingRef.current = false
    lastPointRef.current = null
    if (event?.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
    if (!canvas || !hasSignatureRef.current) return
    const dataUrl = canvas.toDataURL("image/png")
    valueRef.current = dataUrl
    onChange(dataUrl)
  }

  const handlePointerDown = (event: PointerEvent<HTMLCanvasElement>) => {
    event.preventDefault()
    const point = getPoint(event)
    if (!point) return
    event.currentTarget.setPointerCapture(event.pointerId)
    drawingRef.current = true
    lastPointRef.current = point

    const ctx = canvasRef.current?.getContext("2d")
    if (ctx) {
      ctx.beginPath()
      ctx.arc(point.x, point.y, 1.1, 0, Math.PI * 2)
      ctx.fillStyle = "#111827"
      ctx.fill()
      hasSignatureRef.current = true
    }
  }

  const handlePointerMove = (event: PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current) return
    event.preventDefault()

    const canvas = canvasRef.current
    const ctx = canvas?.getContext("2d")
    const point = getPoint(event)
    const previousPoint = lastPointRef.current
    if (!ctx || !point || !previousPoint) return

    ctx.beginPath()
    ctx.moveTo(previousPoint.x, previousPoint.y)
    ctx.lineTo(point.x, point.y)
    ctx.stroke()

    lastPointRef.current = point
    hasSignatureRef.current = true
  }

  const clearSignature = () => {
    const canvas = canvasRef.current
    const ctx = canvas?.getContext("2d")
    if (!canvas || !ctx) return
    const rect = canvas.getBoundingClientRect()
    ctx.fillStyle = "#ffffff"
    ctx.fillRect(0, 0, rect.width, rect.height)
    hasSignatureRef.current = false
    valueRef.current = ""
    onChange("")
  }

  return (
    <div className="space-y-2">
      <div className="overflow-hidden rounded-md border bg-background">
        <canvas
          ref={canvasRef}
          className="block h-36 w-full touch-none cursor-crosshair"
          aria-label="Campo de assinatura"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={finishSignature}
          onPointerCancel={finishSignature}
          onPointerLeave={finishSignature}
        />
      </div>
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-muted-foreground">
          Assine com o dedo, mouse ou caneta.
        </p>
        <Button type="button" variant="ghost" size="sm" onClick={clearSignature}>
          <RotateCcw className="mr-2 h-4 w-4" />
          Limpar
        </Button>
      </div>
    </div>
  )
}
